import { useId, useRef, useState, type FormEvent } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { changeLanguage, isLanguageCode, LANGUAGES, type LanguageCode } from '@/i18n';
import { ApiError } from '@/lib/api';
import { useAuth } from './useAuth';
import { readRememberedVenueCode } from './venueCodeStorage';

/**
 * The wait a locked sign-in names, in whole minutes — or null when the refusal
 * carried no usable `details.retryAfterSeconds`.
 *
 * Rounded UP, and never below one, exactly as the API words its own English
 * message: a wait shorter than the lock sends a cashier back into a refusal,
 * and the last few seconds of a lock must not read "0 minutes".
 */
export function signInLockMinutes(details: unknown): number | null {
  const seconds =
    details !== null && typeof details === 'object'
      ? (details as { retryAfterSeconds?: unknown }).retryAfterSeconds
      : undefined;
  if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return null;
  return Math.max(1, Math.ceil(seconds / 60));
}

/**
 * Whether a refused sign-in could be the venue code's own fault, which opens a
 * remembered (read-only) code for editing, its value kept.
 *
 * A browser re-learns its code only while it is still signed in: `useAuth`
 * reads `/auth/me` after a refresh succeeds. One that was signed out when its
 * venue's code was renamed keeps the old code, and the server answers that
 * code exactly like a wrong password. Left read-only, the code looks settled
 * and the cashier blames the password until the lock. So the neutral 401 and
 * the lock (counted under the code it named) open the field. That tells
 * nobody anything: every wrong answer opens it alike. `venue_code_required` is
 * a 401 too, but only a blank field earns it, and a blank field is never
 * read-only. A 403 `venue_suspended` comes only after a right password, so the
 * code was right; any other 403, a server fault (a 502 while a deploy swaps
 * containers) or a network failure says nothing about it.
 */
function refusalMayBeTheVenueCode(caught: unknown): boolean {
  return (
    caught instanceof ApiError && (caught.status === 401 || caught.code === 'too_many_attempts')
  );
}

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const { signIn } = useAuth();

  // The venue code (EclipseGG platform, 2026-10-19), prefilled from what this
  // browser last learnt from the server (venueCodeStorage.ts). A remembered
  // code is shown read-only: the cashier at a till never chose it, so a stray
  // tap must not change it, and "Change venue" is one press away for the
  // phone that really does move between venues. A refusal the code could
  // have caused opens it again (`refusalMayBeTheVenueCode`).
  const [rememberedVenueCode] = useState(readRememberedVenueCode);
  const [venueCode, setVenueCode] = useState(rememberedVenueCode ?? '');
  const [venueCodeLocked, setVenueCodeLocked] = useState(rememberedVenueCode !== null);
  const venueCodeInput = useRef<HTMLInputElement>(null);
  const venueCodeId = useId();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const currentLanguage = (
    isLanguageCode(i18n.language) ? i18n.language : 'en'
  ) satisfies LanguageCode;

  function failureMessage(caught: unknown): string {
    // A blank code while the server has no fallback (LEGACY_DEFAULT_VENUE_SLUG)
    // is the one 401 that is not a verdict: nothing was checked, so saying
    // what is missing leaks nothing.
    if (
      caught instanceof ApiError &&
      caught.status === 401 &&
      caught.code === 'venue_code_required'
    ) {
      return t('auth.venueCodeRequired');
    }
    // Never distinguish "no such user" — or "no such venue" — from "wrong
    // password": that leaks which usernames and which codes exist. The server
    // answers an unknown code exactly like a wrong password (spec §1), so the
    // one line names all three.
    if (caught instanceof ApiError && caught.status === 401) return t('auth.invalidCredentials');
    // A suspended venue is refused only AFTER a correct password (spec §1,
    // order of checks), and in English; worded here in the cashier's own
    // language for the same reason as the lock below.
    if (caught instanceof ApiError && caught.status === 403 && caught.code === 'venue_suspended') {
      return t('auth.venueSuspended');
    }
    // The sign-in lock (2026-09-23) answers 429 `too_many_attempts`, and its
    // message is English. Printed as it came, the one refusal that tells a
    // cashier to STOP typing passwords reached Arabic and Kurdish screens in a
    // language they may not read. Worded here from the code and the seconds
    // instead. The strings live in `locales/<lang>/auth.json` under
    // `signInLock`, not `auth`: an area file's top-level key replaces the
    // core bundle's whole, and an `auth` there would drop every sign-in label.
    if (caught instanceof ApiError && caught.code === 'too_many_attempts') {
      const minutes = signInLockMinutes(caught.details);
      return minutes === null
        ? t('signInLock.tooManyAttemptsLater')
        : t('signInLock.tooManyAttempts', { count: minutes });
    }
    return caught instanceof Error ? caught.message : t('auth.invalidCredentials');
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await signIn(username, password, venueCode);
    } catch (caught) {
      setError(failureMessage(caught));
      if (refusalMayBeTheVenueCode(caught)) setVenueCodeLocked(false);
    } finally {
      setSubmitting(false);
    }
  }

  // Clears the FIELD, not the stored code (see `forgetVenueCode`): until a
  // sign-in at another venue teaches the browser a new one, a reload brings
  // the old one back. The field is made editable BEFORE it is focused, inside
  // the tap: React would apply both updates only after this handler, and a
  // phone raises no keyboard for a field focused while read-only.
  function changeVenue() {
    flushSync(() => {
      setVenueCode('');
      setVenueCodeLocked(false);
    });
    venueCodeInput.current?.focus();
  }

  return (
    // Transparent wrapper: the blueprint grid on <body> is the backdrop —
    // an opaque fill here hid the design's signature element on the very
    // first screen anyone sees.
    //
    // Centred by `m-auto`, not `items-center`, and the wrapper scrolls: on a
    // phone turned sideways (~330px tall) the card is taller than the screen,
    // and flex centring pushed its top — the title and the language chips —
    // above the viewport where nothing could scroll to it. Auto margins centre
    // while there is room and fall to 0 when there is not.
    <div className="flex h-full overflow-y-auto p-4">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="m-auto w-full max-w-sm rounded-card border border-subtle bg-raised p-6 shadow-xl"
      >
        <h1 className="mb-4 text-center text-xl font-semibold text-content">{t('auth.signIn')}</h1>

        {/*
          Language as centred chips inside the panel (approved sign-in card),
          each in its own script — staff pick their language by recognising
          it, and on this screen there is no top bar to host a dropdown.
        */}
        <div className="mb-6 flex justify-center gap-1.5">
          {Object.entries(LANGUAGES).map(([code, meta]) => (
            <button
              key={code}
              type="button"
              onClick={() => {
                if (isLanguageCode(code)) void changeLanguage(code);
              }}
              aria-pressed={code === currentLanguage}
              lang={code}
              className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${
                code === currentLanguage
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-subtle text-content-secondary hover:bg-hover'
              }`}
            >
              {meta.nativeLabel}
            </button>
          ))}
        </div>

        {/*
          The venue code sits above the username (spec §1). Not `required`:
          blank is legal while the server's deploy-day fallback is set, and the
          server words the refusal (`venue_code_required`) once it is not. No
          capitals, no autocorrect, no spellcheck: a phone keyboard that
          "corrects" a code into a dictionary word fails a sign-in for a reason
          nobody can see. Labelled by `htmlFor`, where the two fields below
          are wrapped, so that "Change venue" is not inside the label.
        */}
        <div className="mb-4">
          <label htmlFor={venueCodeId} className="mb-1 block text-sm text-content-secondary">
            {t('auth.venueCodeLabel')}
          </label>
          <div className="flex items-center gap-2">
            <input
              ref={venueCodeInput}
              id={venueCodeId}
              type="text"
              name="venueCode"
              value={venueCode}
              onChange={(event) => setVenueCode(event.target.value)}
              readOnly={venueCodeLocked}
              autoComplete="organization"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="min-w-0 flex-1 rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary read-only:text-content-secondary"
            />
            {venueCodeLocked ? (
              <button
                type="button"
                data-action="change-venue"
                onClick={changeVenue}
                className="shrink-0 text-sm font-medium text-primary hover:underline"
              >
                {t('auth.changeVenue')}
              </button>
            ) : null}
          </div>
        </div>

        {/*
          No capitals, no autocorrect, no spellcheck here either. Sign-in
          matches a username exactly, and the staff app saves every username
          lowercase, so a phone that capitalised the first letter sent `Owner`:
          refused like a wrong password, for a username that can only be
          `owner`. Autocorrect can change more than the case.
        */}
        <label className="mb-4 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('auth.usernameLabel')}
          </span>
          <input
            type="text"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
            className="w-full rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary"
          />
        </label>

        <label className="mb-6 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('auth.passwordLabel')}
          </span>
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
            className="w-full rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary"
          />
        </label>

        {error ? (
          <p role="alert" className="mb-4 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-full bg-primary px-4 py-2 font-medium text-primary-fg transition-colors hover:bg-primary-hover disabled:opacity-60"
        >
          {submitting ? t('auth.signingIn') : t('auth.signIn')}
        </button>
      </form>
    </div>
  );
}
